import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Paper,
  useTheme,
  useMediaQuery,
  Fade,
  Slide,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import SearchBar from '../components/SearchBar';
import MissionCard from '../components/MissionCard';
import { fetchMissions, selectMissions } from '../redux/slices/missionsSlice';
import './Home.css';

const Home = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  const missions = useSelector(selectMissions);
  const [featuredMissions, setFeaturedMissions] = useState([]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    dispatch(fetchMissions({ page: 1, limit: 6, sort: '-createdAt' }));
  }, [dispatch]);

  useEffect(() => {
    if (Array.isArray(missions)) {
      setFeaturedMissions(missions.slice(0, 6));
    }
  }, [missions]);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoaded(true), 500);
    return () => clearTimeout(timer);
  }, []);

  const handleOrganizationClick = () => {
    navigate('/register?type=organization');
  };

  const handleProfessionalClick = () => {
    navigate('/register?type=professional');
  };

  const handleViewAllMissions = () => {
    navigate('/missions');
  };

  return (
    <>
      <Helmet>
        <title>Mission Sport - Connecter les talents sportifs avec les opportunités</title>
        <meta 
          name="description" 
          content="Plateforme de référence pour les missions sportives en France. Trouvez des professionnels qualifiés ou des opportunités dans le sport." 
        />
        <meta 
          name="keywords" 
          content="mission sport, emploi sport, professionnel sport, entraîneur, kinésithérapeute, arbitre" 
        />
        
        {/* Open Graph */}
        <meta property="og:title" content="Mission Sport - Connecter les talents sportifs" />
        <meta property="og:description" content="Plateforme de référence pour les missions sportives en France" />
        <meta property="og:image" content="/assets/images/logo.png" />
        <meta property="og:url" content="https://missionsport.fr" />
        <meta property="og:type" content="website" />
        
        {/* Twitter Cards */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Mission Sport - Connecter les talents sportifs" />
        <meta name="twitter:description" content="Plateforme de référence pour les missions sportives en France" />
        <meta name="twitter:image" content="/assets/images/logo.png" />
      </Helmet>

      <main className="home-page">
        {/* Hero Section */}
        <Box className="hero-section">
          <Container maxWidth="lg">
            <Box className="hero-content">
              <Fade in={true} timeout={1000}>
                <Box className="hero-text">
                  <Typography
                    variant="h1"
                    component="h1"
                    className="hero-title"
                    sx={{
                      fontSize: {
                        xs: '2.5rem',
                        sm: '3.5rem',
                        md: '4.5rem',
                        lg: '5rem',
                      },
                      fontWeight: 700,
                      lineHeight: 1.1,
                      color: 'text.primary',
                      textAlign: 'center',
                      mb: 5,
                      textShadow: 'none',
                    }}
                  >
                    Connecter les talents,{' '}
                    <Box component="span" sx={{ color: 'text.primary' }}>
                      créer les opportunités
                    </Box>
                  </Typography>
                </Box>
              </Fade>

              {/* Search Bar */}
              <Slide direction="up" in={true} timeout={1200}>
                <Box className="hero-search">
                  <SearchBar variant="hero" />
                </Box>
              </Slide>

              {/* CTA Buttons */}
              <Slide direction="up" in={true} timeout={1400}>
                <Box className="hero-cta">
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleOrganizationClick}
                    className="cta-button cta-organization"
                    sx={{
                      backgroundColor: 'secondary.main',
                      color: 'white',
                      fontSize: {
                        xs: '0.9rem',
                        sm: '1rem',
                        md: '1.1rem',
                      },
                      px: {
                        xs: 3,
                        sm: 4,
                        md: 5,
                      },
                      py: {
                        xs: 1.5,
                        sm: 2,
                      },
                      borderRadius: '50px',
                      fontWeight: 600,
                      textTransform: 'none',
                      boxShadow: '0 4px 20px rgba(255, 152, 0, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: 'secondary.dark',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 25px rgba(255, 152, 0, 0.4)',
                      },
                    }}
                  >
                    Je suis une organisation
                  </Button>
                  
                  <Button
                    variant="contained"
                    size="large"
                    onClick={handleProfessionalClick}
                    className="cta-button cta-professional"
                    sx={{
                      backgroundColor: 'primary.main',
                      color: 'white',
                      fontSize: {
                        xs: '0.9rem',
                        sm: '1rem',
                        md: '1.1rem',
                      },
                      px: {
                        xs: 3,
                        sm: 4,
                        md: 5,
                      },
                      py: {
                        xs: 1.5,
                        sm: 2,
                      },
                      borderRadius: '50px',
                      fontWeight: 600,
                      textTransform: 'none',
                      boxShadow: '0 4px 20px rgba(76, 175, 80, 0.3)',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: 'primary.dark',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 25px rgba(76, 175, 80, 0.4)',
                      },
                    }}
                  >
                    Je suis un professionnel
                  </Button>
                </Box>
              </Slide>
            </Box>
          </Container>
        </Box>

        {/* Featured Missions Section */}
        <Box className="featured-missions-section">
          <Container maxWidth="lg">
            <Fade in={isLoaded} timeout={1000}>
              <Box>
                <Typography
                  variant="h2"
                  component="h2"
                  sx={{
                    fontSize: {
                      xs: '2rem',
                      sm: '2.5rem',
                      md: '3rem',
                    },
                    fontWeight: 700,
                    textAlign: 'center',
                    mb: 2,
                    color: 'text.primary',
                  }}
                >
                  Missions en vedette
                </Typography>
                
                <Typography
                  variant="h6"
                  component="p"
                  sx={{
                    textAlign: 'center',
                    color: 'text.secondary',
                    mb: 6,
                    maxWidth: '600px',
                    mx: 'auto',
                  }}
                >
                  Découvrez les dernières opportunités publiées par nos organisations partenaires
                </Typography>

                <Grid container spacing={4} className="missions-grid">
                  {featuredMissions.map((mission, index) => (
                    <Grid item xs={12} sm={6} lg={4} key={mission.id}>
                      <Slide
                        direction="up"
                        in={isLoaded}
                        timeout={1000 + index * 200}
                      >
                        <Box>
                          <MissionCard mission={mission} variant="default" />
                        </Box>
                      </Slide>
                    </Grid>
                  ))}
                </Grid>

                <Box sx={{ textAlign: 'center', mt: 6 }}>
                  <Button
                    variant="outlined"
                    size="large"
                    onClick={handleViewAllMissions}
                    sx={{
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      fontSize: '1.1rem',
                      px: 4,
                      py: 1.5,
                      borderRadius: '50px',
                      fontWeight: 600,
                      textTransform: 'none',
                      borderWidth: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: 'primary.main',
                        color: 'white',
                        transform: 'translateY(-2px)',
                        boxShadow: '0 6px 25px rgba(76, 175, 80, 0.3)',
                        borderWidth: 2,
                      },
                    }}
                  >
                    Voir toutes les missions
                  </Button>
                </Box>
              </Box>
            </Fade>
          </Container>
        </Box>

        {/* Stats Section */}
        <Box className="stats-section" sx={{ py: 8, mt: 6, mb: 6 }}>
          <Container maxWidth="lg">
            <Grid container spacing={4}>
              <Grid item xs={6} md={3}>
                <Paper className="stat-card">
                  <Typography variant="h3" className="stat-number">
                    500+
                  </Typography>
                  <Typography variant="body1" className="stat-label">
                    Missions publiées
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper className="stat-card">
                  <Typography variant="h3" className="stat-number">
                    200+
                  </Typography>
                  <Typography variant="body1" className="stat-label">
                    Professionnels
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper className="stat-card">
                  <Typography variant="h3" className="stat-number">
                    150+
                  </Typography>
                  <Typography variant="body1" className="stat-label">
                    Organisations
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={6} md={3}>
                <Paper className="stat-card">
                  <Typography variant="h3" className="stat-number">
                    95%
                  </Typography>
                  <Typography variant="body1" className="stat-label">
                    Satisfaction
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          </Container>
        </Box>
      </main>
    </>
  );
};

export default Home;